<template>
  <div class="dashboard">
    <div class="tw:flex tw:flex-row tw:flex-nowrap tw:items-center">
      <FilterBar class="tw:flex tw:grow tw:items-center" v-show="!loading">
        <slot name="filter-bar-left" />
        <ModernColumnFilter
          :defaultFields="defaultFields"
          :appliedFields="appliedFields"
          :fields="fields"
          @change="applyFields" />
        <ViewFilter
          :filters="filters"
          :appliedFilters="appliedFilters"
          :appliedOptions="appliedOptions"
          :showMoreOptions="true"
          :filterOpen="filterOpen"
          :activeFilterCategory="activeFilterCategory"
          @filterClosed="
            filterOpen = false;
            activeFilterCategory = undefined;
          "
          @change="applyFilters" />

        <div class="spacer">&nbsp;</div>

        <Teleport v-if="!loading" defer to="#alternate-toggles" :disabled="!showTogglesOnTable">
          <div v-if="showFilterToggles" class="tw:flex tw:w-full tw:justify-end tw:gap-4 tw:py-0.5 tw:pr-3">
            <toggle
              v-if="showWithoutProgramToggle"
              id="toggle-without-program-only"
              label="Show Policies Without Program Only"
              v-model="showWithoutProgramOnly" />

            <toggle
              id="toggle-policies"
              :label="tenantName() !== '' ? `Show Non-${tenantName()} Policies` : `Show External Policies`"
              v-model="showExternal" />

            <toggle id="toggle-current-only" label="Show Current Only" v-model="showCurrentOnly" />
          </div>
        </Teleport>

        <slot name="filter-bar-right" />
      </FilterBar>

      <slot name="header-right" />
    </div>

    <div v-if="loading" class="tw:space-y-2 tw:px-4 tw:py-4">
      <Skeleton v-for="i in 8" :key="i" shape="rectangle" width="100%" height="48px" />
    </div>

    <div id="policy-details-table-container" class="tw:px-4 tw:py-4" v-if="!loading">
      <slot name="inner" />
      <div id="policy-details-subheader" class="tw:mb-1.5 tw:flex tw:content-start tw:items-start tw:gap-x-2">
        <div class="tw:flex tw:w-full tw:items-center tw:justify-between tw:my-auto">
          <div class="tw:flex tw:items-center tw:gap-2">
            <h2>{{ title }}</h2>
            <MenuDropdown :items="downloadMenuOptions" bottom>
              <template #action>
                <div role="button" tabindex="0" class="tw:btn tw:border tw:border-gray-300 tw:btn-sm">
                  <Download class="tw:h-4 tw:w-4 tw:stroke-primary" />
                </div>
              </template>

              <template #items="{ items, handleClick }">
                <li v-for="item in items" :key="item.label">
                  <a
                    @click="() => handleClick(item)"
                    class="tw:rounded-lg tw:px-4 tw:py-2 tw:text-sm tw:text-gray-700 tw:no-underline">
                    {{ item.label }}
                  </a>
                </li>
              </template>
            </MenuDropdown>
            <div class="tw:flex tw:items-center tw:gap-1">
              <toggle
                id="toggle-include-coverage-lines"
                label="Include Coverage Lines in Download"
                v-model="includeCoverageLines" />
              <Tooltip
                text="Turning on this toggle will include coverage-line level detail for package policies."
                position="top">
                <Info
                  class="tw:h-4 tw:w-4 tw:cursor-help tw:stroke-gray-500"
                  :stroke-width="2" />
              </Tooltip>
            </div>
          </div>

          <div id="alternate-toggles" />

          <slot name="inner-append" />
          <div v-if="!isUserAClient && !clientViewEnabled && allowPolicyUpload">
            <UploadExternalPoliciesModal
              :tenantName="tenantName()"
              :accountID="accountId"
              :isParent="isParent"
              @needs-update="externalPolicyUploaded" />
          </div>
        </div>
        <div v-if="$route.meta.action === 'create-new-program'" class="tw:w-90">
          <TextField
            v-model="searchValue"
            placeholder="Search"
            data-test="account-search"
            autocomplete="off"
            :appendIcon="searchIcon"
            @update:model-value="(v) => search(typeof v === 'string' ? v : String(v))" />
        </div>
      </div>

      <EmptyDisplay
        :src="emptySrc"
        alt="No policies"
        title="No policies found"
        v-if="!policies || policies.length === 0"
        text="The filters applied above are too restrictive. Please remove filters to view more policies." />

      <PolicyDetailTable
        v-if="policies && policies.length > 0"
        :auto-sort="!isPaged"
        :visibleFields="appliedFields || defaultFields"
        :policies="allPolicies"
        :policy-hierarchy="policies"
        :perspective="scenario"
        @needs-update="refreshPolicies"
        :showChildren="showChildren"
        :sorts="sorts"
        @sort="sortByField"
        :extraColumnTitle="extraColumnTitle"
        :policy-document-types="policyDocumentTypes"
        @expand="expand"
        :height="policyDetailsTableHeight"
        :showWithoutProgram="showWithoutProgram"
        :policy-sources="policySources"
        :get-prepend-menu-items="getExtraMenuItems">
        <template v-slot:extraColumn="{ policy }" v-if="!!$slots.extraColumn">
          <slot name="extraColumn" v-bind="{ policy }" />
        </template>
        <template v-slot:extraColumnHeader v-if="!!$slots.extraColumnHeader">
          <slot name="extraColumnHeader" />
        </template>
        <template v-slot:actions="{ policy }" v-if="!!$slots.actions">
          <slot name="actions" v-bind="{ policy }" />
        </template>
      </PolicyDetailTable>

      <div id="policy-pagination-buttons" class="tw:pt-4" v-if="!loading && isPaged">
        <div class="tw:flex tw:gap-4" v-if="isPaged">
          <AppButton
            secondary
            :disabled="!hasPrevPage"
            @click="previousPage"
            data-test="prev-button">
            <CircleChevronLeft :size="20" class="tw:mr-2 tw:text-primary" />
            Previous
          </AppButton>
          <AppButton
            secondary
            :disabled="!hasNextPage"
            @click="nextPage"
            data-test="next-button">
            Next
            <CircleChevronRight :size="20" class="tw:ml-2 tw:text-primary" />
          </AppButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { USStates, type QueryParams } from './PolicyDetails';
import { PropType } from 'vue';
import emptySrc from '@/assets/images/empty.svg';
import PolicyDetailTable from '@/components/PolicyDetailTable.vue';
import {
  accountService,
  carrierService,
  parentAccountService,
  PolicyAllParams,
  policyService,
  policySourceService,
  referenceService,
  wholesalerGroupService,
} from '@/services/api';
import { documentService } from '@/services/v2/DocumentService';
import { accountService as accountServiceV2 } from '@/services/v2/AccountService';
import { DownloadCSV, TransformPoliciesToCSV } from '@/utilities/Download';
import FilterBar from '@/components/FilterBar.vue';
import ModernColumnFilter from '@/components/ModernColumnFilter.vue';
import ViewFilter from '@/components/ViewFilter.vue';
import UploadExternalPoliciesModal from '@/components/v2/UploadExternalPoliciesModal.vue';
import { EpicPolicy } from '@/types/tower/Domain';
import {
  FlattenPolicies,
  GetPoliciesAsHierarchy,
  PoliciesAsHierarchy,
  PolicyStats,
  PolicyWithChildren,
} from '@/utilities/Policies';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import { calculatePPM } from '@/utilities/tower/Calculations';
import { LabelValue } from '@/types/label-value';
import { policyDetailsType } from '@/types/PolicyDetailsType';
import { DocumentType } from '@/types/v2/Document';
import { DateTime } from 'luxon';
import store from '@/store';
import { IsUserAClient, IsUserAnAdmin, IsUserABroker, tenantName } from '@/utilities/AuthUtils';
import { PolicyDetailsFilter } from '@/types/PolicyDetailsFilter';
import { RouteLocationRaw, useRoute } from 'vue-router';
import Toggle from '@/components/v2/Toggle.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import TextField from '@/components/v2/TextField.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import { LabelFilterType } from '@/types/components/FilterType';
import MenuDropdown from '@/components/v2/MenuDropdown.vue';
import { CircleChevronLeft, CircleChevronRight, Download, Info, Search } from 'lucide-vue-next';
import { registerResizeItem } from '@/utilities/resize';
import { WholesalerGroupPoliciesByIdParams } from '@/types/WholesalerGroup';
import { CarrierPoliciesByIdParams } from '@/types/Carrier';
import { MutationPayload } from 'vuex';
import { StoreMutationType } from '@/types/StoreMutationType';
import {
  AppliedFilter,
  FilterAccountStates,
  FilterAttachment,
  FilterCACEntity,
  FilterCarrierGroup,
  FilterClientAttributes,
  FilterCoverageType,
  FilterField,
  FilterIndustry,
  FilterMoreOptions,
  FilterRetention,
  FilterSICIndustry,
  FilterValueType,
  FilterWholesalerGroup,
  FilterDepartments,
  FilterOwnershipType,
} from '@/types/ViewFilter.js';
import { PolicySource } from '@/types/PolicySource';

function joinAddress(
  addr1?: string,
  addr2?: string,
  addr3?: string,
  city?: string,
  state?: string,
  postalCode?: string,
): string {
  let addr = '';

  if (addr1) addr = `${addr1}\n`;
  if (addr2) addr = `${addr}${addr2}\n`;
  if (addr3) addr = `${addr}${addr3}\n`;
  if (city) addr = `${addr}${city}`;
  if (state) addr = `${addr}, ${state}`;
  if (postalCode) addr = `${addr} ${postalCode}`;

  return addr;
}

function formatAddress(p: EpicPolicy): EpicPolicy {
  return {
    ...p,
    obligeeAddress: joinAddress(
      p.obligeeAddress1,
      p.obligeeAddress2,
      p.obligeeAddress3,
      p.obligeeCity,
      p.obligeeState,
      p.obligeePostalCode,
    ),
    principalAddress: joinAddress(
      p.principalAddress1,
      p.principalAddress2,
      p.principalAddress3,
      p.principalCity,
      p.principalState,
      p.principalPostalCode,
    ),
    underwritingAddress: joinAddress(
      p.underwritingAddress1,
      p.underwritingAddress2,
      p.underwritingAddress3,
      p.underwritingCity,
      p.underwritingState,
      p.underwritingPostalCode,
    ),
  };
}

export default {
  name: 'PolicyDetails',
  props: {
    title: { type: String, required: false, default: 'Details' },
    scenario: {
      type: String,
      validator(value: policyDetailsType) {
        return [
          policyDetailsType.All,
          policyDetailsType.Account,
          policyDetailsType.ParentAccount,
          policyDetailsType.Carrier,
          policyDetailsType.Wholesaler,
          policyDetailsType.Program,
        ].includes(value);
      },
    },
    startDate: { type: String as PropType<string | undefined | null>, required: false },
    endDate: { type: String as PropType<string | undefined | null>, required: false },
    preloadedPolicies: { type: Array as PropType<EpicPolicy[] | undefined>, required: false },
    extraColumnTitle: { type: String, required: false, default: '' },
    hideDownload: Boolean,
    showOnlyMoreFiltersSection: Boolean,
    showExpiredAtStart: Boolean,
    hideMoreFilters: Boolean,
    showFilterToggles: { type: Boolean, default: true },
    showTogglesOnTable: { type: Boolean, default: false },
    showWithoutProgramToggle: { type: Boolean, default: false },
    showWithoutProgram: { type: Boolean, default: false },
    showMoreFilters: { type: Boolean, default: false },
    searchQuery: { type: String, required: false, default: '' },
    searchField: { type: String, required: false, default: '' },
    /** Optional function (policy) => MenuItem[] for extra items at the top of each row's dropdown (same style as built-in items). */
    getExtraMenuItems: { type: Function as PropType<(policy: EpicPolicy) => unknown[]>, required: false },
  },
  data: () => ({
    searchTimeoutId: 0,
    loading: true,
    emptySrc,
    allPolicies: [] as EpicPolicy[],
    policies: [] as PolicyWithChildren[],
    currentPage: 1,
    hasPrevPage: false as boolean,
    hasNextPage: false as boolean,
    appliedFields: undefined as string[] | undefined,
    activeFilterCategory: undefined as string | undefined,
    filterOpen: false as boolean,
    appliedFilters: [] as FilterField<FilterValueType>[],
    appliedFilterBadges: [] as AppliedFilter[],
    appliedOptions: undefined as FilterMoreOptions | undefined,
    showExternalApplied: false,
    showExpiredApplied: false,
    showExternal: false,
    showExpired: false,
    showCurrentOnly: true,
    showWithoutProgramOnly: false,
    includeCoverageLines: false,
    daysUntilExpiration: null as number | null,
    extraFilters: {
      showPrimary: false,
      showExcess: false,
      showRunoff: false,
      showNoRunoff: false,
      showFavorites: false,
    },
    moreFilters: {
      showExternal: false,
      showExpired: false,
      daysUntilExpiration: null as number | null,
      showPrimary: false,
      showExcess: false,
      showRunoff: false,
      showNoRunoff: false,
      showFavorites: false,
    },
    allCoverageTypes: [] as Record<string, string>[],
    coverageTypes: [] as LabelFilterType<string>[],
    selectedCoverageTypes: [] as string[],
    appliedCoverageTypes: [] as string[],
    allSicIndustries: [] as Record<string, string>[],
    selectedSicIndustries: [] as string[],
    appliedSicIndustries: [] as string[],
    allIndustries: [] as Record<string, string>[],
    selectedIndustries: [] as string[],
    appliedIndustries: [] as string[],
    allWholesalerGroups: [] as Record<string, string>[],
    selectedWholesalerGroups: [] as string[],
    appliedWholesalerGroups: [] as string[],
    selectedCarriers: [] as string[],
    allCarriers: [] as Record<string, string>[],
    appliedCarriers: [] as string[],
    allDepartments: [] as LabelFilterType<string>[],
    appliedDepartments: [] as string[],
    selectedDepartments: [] as string[],
    selectedOwnershipTypes: [] as string[],
    appliedOwnershipTypes: [] as string[],
    selectedAttachmentRange: [] as number[],
    appliedAttachmentRange: [] as number[],
    selectedRetentionRange: [] as number[],
    appliedRetentionRange: [] as number[],
    selectedAccountAttributes: [] as string[],
    appliedAccountAttributes: [] as string[],
    allAgencies: [] as LabelValue<string>[],
    selectedAgencies: [] as string[],
    appliedAgencies: [] as string[],
    sorts: [
      { key: 'effectiveDate', direction: 'descending' },
      { key: 'policyID', direction: 'ascending' },
      { key: 'coverageType', direction: 'ascending' },
      { key: 'attachmentPoint', direction: 'ascending' },
    ] as { key: string; direction: 'ascending' | 'descending' }[],
    accountAttributeTypes: [] as LabelValue<string>[],
    policyDocumentTypes: [] as DocumentType[],
    searchValue: '',
    searchIcon: Search,
    policyStats: undefined as PolicyStats | undefined,
    $route: useRoute(),
    queryParams: {} as QueryParams,
    includeIndustryChildren: false,
    listOfUSStates: [] as LabelValue<string>[],
    appliedAccountStates: [] as string[],
    selectedAccountStates: [] as string[],
    policySources: [] as PolicySource[],
  }),
  components: {
    UploadExternalPoliciesModal: UploadExternalPoliciesModal,
    MenuDropdown,
    EmptyDisplay,
    PolicyDetailTable,
    FilterBar,
    ModernColumnFilter,
    ViewFilter,
    Toggle,
    Skeleton,
    TextField,
    AppButton,
    Tooltip,
    Download,
    Info,
    CircleChevronLeft,
    CircleChevronRight,
  },
  computed: {
    policyDetailsTableHeight(): number | undefined {
      const registry = store.state.resizerRegistry;
      switch (this.scenario) {
        case policyDetailsType.All:
          return (
            window.innerHeight -
            (registry['policy-list-header']?.height ?? 0) -
            (registry['filter-bar']?.height ?? 0) -
            (registry['header']?.height ?? 0) -
            (registry['policy-details-subheader']?.height ?? 0) -
            (registry['policy-pagination-buttons']?.height ?? 0) -
            50
          );
        case policyDetailsType.Account:
          return (
            window.innerHeight -
            (registry['account-header']?.height ?? 0) -
            (registry['account-tabs']?.height ?? 0) -
            (registry['filter-bar']?.height ?? 0) -
            (registry['header']?.height ?? 0) -
            (registry['policy-details-subheader']?.height ?? 0) -
            40
          );
        case policyDetailsType.ParentAccount:
          return (
            window.innerHeight -
            (registry['parent-account-header']?.height ?? 0) -
            (registry['parent-account-tabs']?.height ?? 0) -
            (registry['filter-bar']?.height ?? 0) -
            (registry['header']?.height ?? 0) -
            (registry['policy-details-subheader']?.height ?? 0) -
            40
          );
        case policyDetailsType.Carrier:
          return (
            window.innerHeight -
            (registry['carrier-header']?.height ?? 0) -
            (registry['carrier-tabs']?.height ?? 0) -
            (registry['filter-bar']?.height ?? 0) -
            (registry['header']?.height ?? 0) -
            (registry['policy-details-subheader']?.height ?? 0) -
            88
          );
        case policyDetailsType.Wholesaler:
        case policyDetailsType.CustomWholesaler:
          return (
            window.innerHeight -
            (registry['wholesaler-header']?.height ?? 0) -
            (registry['wholesaler-tabs']?.height ?? 0) -
            (registry['filter-bar']?.height ?? 0) -
            (registry['header']?.height ?? 0) -
            (registry['policy-details-subheader']?.height ?? 0) -
            88
          );
        default:
          return undefined;
      }
    },
    pageSize() {
      switch (this.scenario) {
        case policyDetailsType.Carrier:
        case policyDetailsType.Wholesaler:
        case policyDetailsType.CustomWholesaler:
          return 100;
        default:
          return 50;
      }
    },
    accountId() {
      return this.$route.params.id as string;
    },
    hideUnpublishedRoles() {
      return IsUserAClient(store.state.user);
    },
    isPaged() {
      return (
        this.scenario &&
        [
          policyDetailsType.All,
          policyDetailsType.Wholesaler,
          policyDetailsType.CustomWholesaler,
          policyDetailsType.Carrier,
        ].includes(this.scenario as policyDetailsType)
      );
    },
    allowCoverageTypeFilter() {
      return [policyDetailsType.All, policyDetailsType.ParentAccount, policyDetailsType.Account].includes(
        this.scenario as policyDetailsType,
      );
    },
    allowSicIndustryFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowIndustryFilter() {
      return [
        policyDetailsType.All,
        policyDetailsType.Carrier,
        policyDetailsType.Wholesaler,
        policyDetailsType.CustomWholesaler,
      ].includes(this.scenario as policyDetailsType);
    },
    allowWholesalerGroupFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowCarrierGroupFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowAttachmentFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowRetentionFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowAccountAttributesFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowAgencyFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowAccountStatesFilter() {
      return this.scenario === policyDetailsType.All;
    },
    allowDepartmentsFilter() {
      return this.scenario === policyDetailsType.All;
    },
    attachments() {
      return PoliciesAsHierarchy(this.allPolicies)
        .filter((p) => p.attachmentPoint)
        .map((p) => p.attachmentPoint) as number[];
    },
    filteredCoverageTypes(): string[] | undefined {
      if (Array.isArray(this.appliedCoverageTypes) && this.appliedCoverageTypes.length > 0) {
        return this.appliedCoverageTypes.filter((type): type is string => type !== undefined);
      }
      return undefined;
    },
    sicIndustries(): LabelFilterType<string>[] {
      const industries =
        this.allSicIndustries.length > 0
          ? this.allSicIndustries
          : Array.from(
              this.allPolicies
                .filter((p) => p.topLevelIndustry)
                .map((p) => p.topLevelIndustry as string)
                .reduce((acc, v) => acc.add(v), new Set<string>()),
            )
              .map((v) => ({ text: v, value: v }))
              .sort((a, b) => a.text.localeCompare(b.text));

      return industries.map((i) => ({ value: i.value, label: i.text }));
    },
    filteredSicIndustries(): string[] | undefined {
      if (Array.isArray(this.appliedSicIndustries) && this.appliedSicIndustries.length > 0) {
        return this.appliedSicIndustries.filter((industry): industry is string => industry !== undefined);
      }
      return undefined;
    },
    industries(): LabelFilterType<string>[] {
      const industries =
        this.allIndustries.length > 0
          ? this.allIndustries
          : Array.from(
              this.allPolicies
                .filter((p) => p.topLevelIndustry)
                .map((p) => p.topLevelIndustry as string)
                .reduce((acc, v) => acc.add(v), new Set<string>()),
            )
              .map((v) => ({ text: v, value: v }))
              .sort((a, b) => a.text.localeCompare(b.text));

      return industries.map((i) => ({ value: i.value, label: i.text }));
    },
    filteredIndustries(): string[] | undefined {
      if (Array.isArray(this.appliedIndustries) && this.appliedIndustries.length > 0) {
        return this.appliedIndustries.filter((industry): industry is string => industry !== undefined);
      }
      return undefined;
    },
    wholesalerGroups(): LabelFilterType<string>[] {
      const wholesalerGroups =
        this.allWholesalerGroups.length > 0
          ? this.allWholesalerGroups
          : Array.from(
              this.allPolicies
                .filter((p) => p.wholesalerGroup)
                .map((p) => p.wholesalerGroup as string)
                .reduce((acc, v) => acc.add(v), new Set<string>()),
            )
              .map((v) => ({ text: v, value: v }))
              .sort((a, b) => a.text.localeCompare(b.text));

      return wholesalerGroups.map((i) => ({ value: i.value, label: i.text }));
    },
    filteredWholesalerGroups(): string[] | undefined {
      if (Array.isArray(this.appliedWholesalerGroups) && this.appliedWholesalerGroups.length > 0) {
        return this.appliedWholesalerGroups.filter((wg): wg is string => wg !== undefined);
      }
      return undefined;
    },
    filteredAgencies(): string[] | undefined {
      if (Array.isArray(this.appliedAgencies) && this.appliedAgencies.length > 0) {
        return this.appliedAgencies.filter((agency): agency is string => agency !== undefined);
      }
      return undefined;
    },
    filteredAccountStates(): string[] | undefined {
      if (Array.isArray(this.appliedAccountStates) && this.appliedAccountStates.length > 0) {
        return this.appliedAccountStates.filter((state): state is string => state !== undefined);
      }
      return undefined;
    },
    filteredDepartments(): string[] | undefined {
      if (Array.isArray(this.appliedDepartments) && this.appliedDepartments.length > 0) {
        return this.appliedDepartments.filter((dept): dept is string => dept !== undefined);
      }
      return undefined;
    },
    carriers(): LabelFilterType<string>[] {
      const carriers =
        this.allCarriers.length > 0
          ? this.allCarriers
          : Array.from(
              this.allPolicies
                .filter((p) => p.carrierGroup)
                .map((p) => p.carrierGroup as string)
                .reduce((acc, v) => acc.add(v), new Set<string>()),
            )
              .map((v) => ({ text: v, value: v }))
              .sort((a, b) => a.text.localeCompare(b.text));

      return carriers.map((i) => ({ value: i.value, label: i.text }));
    },
    filteredCarriers(): string[] | undefined {
      if (Array.isArray(this.appliedCarriers) && this.appliedCarriers.length > 0) {
        return this.appliedCarriers.filter((carrier): carrier is string => carrier !== undefined);
      }
      return undefined;
    },
    attachmentRange(): { min: number; max: number } | undefined {
      if (Array.isArray(this.appliedAttachmentRange) && this.appliedAttachmentRange.length === 2) {
        return {
          min: this.appliedAttachmentRange[0],
          max: this.appliedAttachmentRange[1],
        };
      }
      return undefined;
    },
    retentionRange(): { min: number; max: number } | undefined {
      if (Array.isArray(this.appliedRetentionRange) && this.appliedRetentionRange.length === 2) {
        return {
          min: this.appliedRetentionRange[0],
          max: this.appliedRetentionRange[1],
        };
      }
      return undefined;
    },
    filteredAccountAttributes(): string[] {
      if (Array.isArray(this.appliedAccountAttributes) && this.appliedAccountAttributes.length > 0) {
        return this.appliedAccountAttributes;
      }
      return [];
    },
    ownershipType(): string[] | undefined {
      if (this.appliedOwnershipTypes.length > 0) {
        return this.appliedOwnershipTypes;
      }
      return undefined;
    },
    runoffFlag(): string[] | undefined {
      let runoff: string[] = [];
      if (this.extraFilters.showRunoff) {
        runoff.push('1');
      }
      if (this.extraFilters.showNoRunoff) {
        runoff.push('0');
      }
      return runoff.length > 0 ? runoff : undefined;
    },
    peFlag(): string[] | undefined {
      let pe: string[] = [];
      if (this.extraFilters.showPrimary) {
        pe.push('Primary');
      }
      if (this.extraFilters.showExcess) {
        pe.push('Excess');
      }
      return pe.length > 0 ? pe : undefined;
    },
    daysUntilExpirationValue(): number | undefined {
      return this.daysUntilExpiration ? Number(this.daysUntilExpiration) : undefined;
    },
    showChildren() {
      return this.scenario === policyDetailsType.ParentAccount;
    },
    allowPolicyUpload() {
      switch (this.scenario) {
        case policyDetailsType.Account:
        case policyDetailsType.ParentAccount:
        case policyDetailsType.Program:
          return true;
      }
      return false;
    },
    externalFilterActive() {
      for (let key in this.extraFilters) {
        if ((this.extraFilters as Record<string, boolean>)[key]) {
          return true;
        }
      }
      return false;
    },
    allFilters() {
      return this.scenario === policyDetailsType.All;
    },
    clientViewEnabled() {
      return store.state.clientViewState.isClientViewEnabled;
    },
    isUserAClient() {
      return IsUserAClient(store.state.user);
    },
    fields() {
      const stringComparer =
        (field: string) =>
        (direction: 'ascending' | 'descending') =>
        (a: PolicyWithChildren, b: PolicyWithChildren) => {
          if (direction === 'ascending') {
            return ('' + a[field]).localeCompare('' + b[field]);
          } else {
            return ('' + b[field]).localeCompare('' + a[field]);
          }
        };
      const numberComparer =
        (field: string) =>
        (direction: 'ascending' | 'descending') =>
        (a: PolicyWithChildren, b: PolicyWithChildren) => {
          if (direction === 'ascending') {
            return ((a[field] as number) || 0) - ((b[field] as number) || 0);
          } else {
            return ((b[field] as number) || 0) - ((a[field] as number) || 0);
          }
        };
      const ppmComparer =
        () => (direction: 'ascending' | 'descending') => (a: PolicyWithChildren, b: PolicyWithChildren) => {
          if (direction === 'ascending') {
            return (calculatePPM(a) || -1) - (calculatePPM(b) || -1);
          } else {
            return (calculatePPM(b) || -1) - (calculatePPM(a) || -1);
          }
        };
      const amBestComparer =
        () => (direction: 'ascending' | 'descending') => (a: PolicyWithChildren, b: PolicyWithChildren) => {
          if (direction === 'ascending') {
            return (a.carrierRatingIndex || -1) - (b.carrierRatingIndex || -1);
          } else {
            return (b.carrierRatingIndex || -1) - (a.carrierRatingIndex || -1);
          }
        };

      const accountKey = this.scenario === policyDetailsType.ParentAccount ? 'childCompany' : 'accountName';
      type StackName = 'Policy' | 'Carrier' | 'Transaction' | 'Surety' | 'Service';

      const list: {
        stack: StackName;
        value: string;
        text: string;
        comparer?: (direction: 'ascending' | 'descending') => (a: PolicyWithChildren, b: PolicyWithChildren) => number;
      }[] = [];

      list.push({ stack: 'Policy', value: 'year', text: 'Year', comparer: numberComparer('year') });
      list.push({ stack: 'Policy', value: accountKey, text: 'Account Name', comparer: stringComparer(accountKey) });

      if (this.scenario === policyDetailsType.All) {
        list.push({
          stack: 'Policy',
          value: 'accountAttributes',
          text: 'Client Attributes',
          comparer: stringComparer('accountAttributes'),
        });
        list.push({
          stack: 'Policy',
          value: 'parentAccountName',
          text: 'Parent Account Name',
          comparer: stringComparer('parentAccountName'),
        });
        list.push({
          stack: 'Policy',
          value: 'topLevelIndustry',
          text: 'SIC Industry',
          comparer: stringComparer('topLevelIndustry'),
        });
        list.push({
          stack: 'Policy',
          value: 'ownershipType',
          text: 'Ownership Type',
          comparer: stringComparer('ownershipType'),
        });
      }

      list.push({
        stack: 'Policy',
        value: 'policyType',
        text: 'Policy Type',
        comparer: stringComparer('policyType'),
      });
      list.push({
        stack: 'Policy',
        value: 'coverageType',
        text: 'Coverage Type',
        comparer: stringComparer('coverageType'),
      });
      list.push({
        stack: 'Policy',
        value: 'product',
        text: 'Product',
        comparer: stringComparer('product'),
      });
      list.push({
        stack: 'Policy',
        value: 'description',
        text: 'Description',
        comparer: stringComparer('description'),
      });
      list.push({
        stack: 'Policy',
        value: 'accountState',
        text: 'Client State',
        comparer: stringComparer('accountState'),
      });
      // Only show department and profit center columns to Admin and Broker users
      if (IsUserAnAdmin(store.state.user) || IsUserABroker(store.state.user)) {
        list.push({
          stack: 'Transaction',
          value: 'department',
          text: 'Department',
          comparer: stringComparer('department'),
        });
        list.push({
          stack: 'Transaction',
          value: 'profitCenter',
          text: 'Profit Center',
          comparer: stringComparer('profitCenter'),
        });
      }

      // Add industry column whenever industry filter is visible
      if (this.allowIndustryFilter) {
        list.push({
          stack: 'Policy',
          value: 'genericIndustry',
          text: 'Industry',
          comparer: stringComparer('industry'),
        });
      }

      if (this.scenario !== policyDetailsType.Carrier) {
        list.push({
          stack: 'Carrier',
          value: 'carrierGroup',
          text: 'Carrier',
          comparer: stringComparer('carrierGroup'),
        });
      }

      list.push({
        stack: 'Carrier',
        value: 'issuingCompanyName',
        text: 'Issuing Company',
        comparer: stringComparer('issuingCompanyName'),
      });
      list.push({ stack: 'Carrier', value: 'carrierRating', text: 'AM Best Rating', comparer: amBestComparer() });

      if (!IsUserAClient(store.state.user)) {
        list.push({
          stack: 'Carrier',
          value: 'syndicate',
          text: 'Syndicate Number',
          comparer: stringComparer('syndicate'),
        });
      }

      list.push({
        stack: 'Carrier',
        value: 'syndicateAgent',
        text: 'Lead Syndicate',
        comparer: stringComparer('syndicateAgent'),
      });

      list.push({
        stack: 'Carrier',
        value: 'wholesaler',
        text: 'Wholesaler',
        comparer: stringComparer('wholesaler'),
      });

      list.push({
        stack: 'Carrier',
        value: 'wholesalerGroup',
        text: 'Wholesaler Group',
        comparer: stringComparer('wholesalerGroup'),
      });

      if (!IsUserAClient(store.state.user)) {
        list.push({
          stack: 'Carrier',
          value: 'underwriterName',
          text: 'Underwriter',
          comparer: stringComparer('underwriterName'),
        });
      }

      list.push({ stack: 'Policy', value: 'aggregateLimit', text: 'Aggregate Limit', comparer: numberComparer('aggregateLimit') });
      list.push({
        stack: 'Policy',
        value: 'occurrenceLimit',
        text: 'Occurrence Limit',
        comparer: numberComparer('occurrenceLimit'),
      });
      list.push({
        stack: 'Policy',
        value: 'attachmentPoint',
        text: 'Attachment Point',
        comparer: numberComparer('attachmentPoint'),
      });
      list.push({ stack: 'Policy', value: 'retention', text: 'Retention', comparer: numberComparer('retention') });
      list.push({ stack: 'Policy', value: 'premium', text: 'Premium', comparer: numberComparer('premium') });
      list.push({
        stack: 'Policy',
        value: 'premiumAnnualized',
        text: 'Premium (Annualized)',
        comparer: numberComparer('premiumAnnualized'),
      });
      list.push({ stack: 'Policy', value: 'ppm', text: 'PPM', comparer: ppmComparer() });
      list.push({
        stack: 'Policy',
        value: 'annualizedPPM',
        text: 'PPM (Annualized)',
        comparer: numberComparer('annualizedPPM'),
      });

      if (this.scenario !== policyDetailsType.Carrier) {
        list.push({ stack: 'Policy', value: 'ilf', text: 'ILF' });
      }

      if (!IsUserAClient(store.state.user)) {
        list.push({
          stack: 'Transaction',
          value: 'auditAmt',
          text: 'Audit Amount',
          comparer: numberComparer('auditAmt'),
        });
      }

      list.push({
        stack: 'Policy',
        value: 'quotaShareTotal',
        text: 'Total Layer Limit',
        comparer: numberComparer('quotaShareTotal'),
      });
      list.push({ stack: 'Policy', value: 'policyID', text: 'Policy Number', comparer: stringComparer('policyID') });
      list.push({
        stack: 'Policy',
        value: 'effectiveDate',
        text: 'Effective Date',
        comparer: stringComparer('effectiveDate'),
      });
      list.push({
        stack: 'Policy',
        value: 'expirationDate',
        text: 'Expiration Date',
        comparer: stringComparer('expirationDate'),
      });
      list.push({ stack: 'Policy', value: 'sideAType', text: 'Side A', comparer: stringComparer('sideAType') });
      list.push({ stack: 'Policy', value: 'runoff', text: 'Runoff', comparer: stringComparer('runoff') });

      if (!IsUserAClient(store.state.user)) {
        list.push({ stack: 'Policy', value: 'peFlag', text: 'Layer', comparer: stringComparer('peFlag') });
        list.push({
          stack: 'Policy',
          value: 'lineStatusCode',
          text: 'Policy Status',
          comparer: stringComparer('lineStatusCode'),
        });
        list.push({
          stack: 'Transaction',
          value: 'billMode',
          text: 'Bill Mode',
          comparer: stringComparer('billMode'),
        });
        list.push({
          stack: 'Transaction',
          value: 'agencyName',
          text: 'CAC Entity',
          comparer: stringComparer('agencyName'),
        });
        list.push({
          stack: 'Transaction',
          value: 'commissionBilled',
          text: 'Commission Billed',
          comparer: numberComparer('commissionBilled'),
        });
        list.push({
          stack: 'Transaction',
          value: 'feesBilled',
          text: 'Fees Billed',
          comparer: numberComparer('feesBilled'),
        });
        list.push({
          stack: 'Transaction',
          value: 'computedRevenue',
          text: 'Revenue',
          comparer: numberComparer('computedRevenue'),
        });
        list.push({
          stack: 'Transaction',
          value: 'enteredDate',
          text: 'Entered Date',
          comparer: stringComparer('enteredDate'),
        });
        list.push({
          stack: 'Transaction',
          value: 'premiumPayableName',
          text: 'Premium Payable',
          comparer: stringComparer('premiumPayableName'),
        });
        list.push({
          stack: 'Transaction',
          value: 'rebateAmt',
          text: 'Rebate Amount',
          comparer: numberComparer('rebateAmt'),
        });
      }

      // Servicing Roles

      list.push({
        stack: 'Service',
        value: 'accountManagerRole',
        text: 'Account Manager',
        comparer: stringComparer('accountManagerRole'),
      });

      // Only show Secondary Account Manager to Admin and Broker users
      if (IsUserAnAdmin(store.state.user) || IsUserABroker(store.state.user)) {
        list.push({
          stack: 'Service',
          value: 'secondaryAccountManagerRole',
          text: 'Secondary Account Manager',
          comparer: stringComparer('secondaryAccountManagerRole'),
        });
      }

      list.push({
        stack: 'Service',
        value: 'accountExecutiveRole',
        text: 'Account Executive',
        comparer: stringComparer('accountExecutiveRole'),
      });
      list.push({
        stack: 'Service',
        value: 'brokerRole',
        text: 'Broker',
        comparer: stringComparer('brokerRole'),
      });
      list.push({
        stack: 'Service',
        value: 'claimsRole',
        text: 'Claims',
        comparer: stringComparer('claimsRole'),
      });
      list.push({
        stack: 'Service',
        value: 'clientLeadRole',
        text: 'Client Lead',
        comparer: stringComparer('clientLeadRole'),
      });

      // Surety

      list.push({
        stack: 'Surety',
        value: 'currTermAmount',
        text: 'Current Term Amount',
        comparer: numberComparer('currTermAmount'),
      });
      list.push({
        stack: 'Surety',
        value: 'obligeeName',
        text: 'Obligee Name',
        comparer: numberComparer('obligeeName'),
      });
      list.push({
        stack: 'Surety',
        value: 'obligeeAddress',
        text: 'Obligee Address',
        comparer: numberComparer('obligeeAddress'),
      });
      list.push({
        stack: 'Surety',
        value: 'principalName',
        text: 'Principal Name',
        comparer: numberComparer('principalName'),
      });
      list.push({
        stack: 'Surety',
        value: 'principalAddress',
        text: 'Principal Address',
        comparer: numberComparer('principalAddress'),
      });
      list.push({
        stack: 'Surety',
        value: 'underwritingName',
        text: 'Underwriting Name',
        comparer: numberComparer('underwritingName'),
      });
      list.push({
        stack: 'Surety',
        value: 'underwritingAddress',
        text: 'Underwriting Address',
        comparer: numberComparer('underwritingAddress'),
      });
      list.push({ stack: 'Surety', value: 'cancelable', text: 'Cancelable', comparer: numberComparer('cancelable') });
      list.push({ stack: 'Surety', value: 'netAmount', text: 'Net Amount', comparer: numberComparer('netAmount') });

      return list;
    },
    filters() {
      const list: FilterField<FilterValueType>[] = [];

      if (this.allowCoverageTypeFilter) list.push({ category: 'Coverage Type', values: this.coverageTypes });
      if (this.allowSicIndustryFilter) list.push({ category: 'SIC Industry', values: this.sicIndustries });
      if (this.allowIndustryFilter) {
        list.push({
          category: 'Industry',
          values: this.industries,
          options: [
            {
              category: 'Industry',
              label: 'Include Children',
              type: 'toggle',
            },
          ],
        });
      }
      if (this.allowWholesalerGroupFilter) list.push({ category: 'Wholesaler Group', values: this.wholesalerGroups });
      if (this.allowCarrierGroupFilter) list.push({ category: 'Carrier Group', values: this.carriers });
      if (this.allowAttachmentFilter) {
        list.push({
          category: 'Attachment',
          description: 'Filter policies by attachment point',
          values: [],
          range: [0, this.staticMaxAttachmentPoint ?? 0],
          prefix: '$',
        });
      }
      if (this.allowRetentionFilter) {
        list.push({
          category: 'Retention',
          description: 'Filter policies by retention value',
          values: [],
          range: [0, this.staticMaxRetention ?? 0],
          prefix: '$',
        });
      }
      if (this.allowAccountAttributesFilter)
        list.push({ category: 'Client Attributes', values: this.accountAttributeTypes });
      if (this.allowAgencyFilter) list.push({ category: 'CAC Entity', values: this.allAgencies });
      if (this.allowAccountStatesFilter) list.push({ category: FilterAccountStates, values: this.listOfUSStates });
      if (this.allowDepartmentsFilter) list.push({ category: FilterDepartments, values: this.allDepartments });
      if (this.scenario === policyDetailsType.All) list.push({ category: 'Ownership Type', values: this.ownershipTypes });

      return list;
    },
    orderedColumnValues() {
      return this.fields.map((f) => f.value);
    },
    coverageTypeValues() {
      return this.coverageTypes.map((c) => c.value);
    },
    sicIndustriesValues() {
      return this.sicIndustries.map((i) => i.value);
    },
    industriesValues() {
      return this.industries.map((i) => i.value);
    },
    wholesalerGroupsValues() {
      return this.wholesalerGroups.map((w) => w.value);
    },
    carrierGroupValues() {
      return this.carriers.map((c) => c.value);
    },
    clientAttributesValues() {
      return this.accountAttributeTypes.map((a) => a.value);
    },
    agenciesValues() {
      return this.allAgencies.map((a) => a.value);
    },
    departmentsValues() {
      return this.allDepartments.map((d) => d.value);
    },
    ownershipTypes() {
      return [
        { value: 'Public', label: 'Public' },
        { value: 'Private', label: 'Private' },
        { value: 'Not Specified', label: 'Not Specified' },
      ];
    },
    defaultFields() {
      switch (this.scenario) {
        case policyDetailsType.Account:
          return [
            'coverageType',
            'description',
            'policyID',
            'carrierGroup',
            'effectiveDate',
            'expirationDate',
            'retention',
            'attachmentPoint',
            'occurrenceLimit',
            'aggregateLimit',
            'premium',
          ];
        case policyDetailsType.Carrier:
          return [
            'accountName',
            'coverageType',
            'description',
            'policyID',
            'effectiveDate',
            'expirationDate',
            'retention',
            'attachmentPoint',
            'occurrenceLimit',
            'aggregateLimit',
            'premium',
          ];
        case policyDetailsType.ParentAccount:
          return [
            'childCompany',
            'coverageType',
            'description',
            'policyID',
            'carrierGroup',
            'effectiveDate',
            'expirationDate',
            'retention',
            'attachmentPoint',
            'occurrenceLimit',
            'aggregateLimit',
            'premium',
          ];
        default:
          return [
            'accountName',
            'coverageType',
            'description',
            'policyID',
            'carrierGroup',
            'effectiveDate',
            'expirationDate',
            'retention',
            'attachmentPoint',
            'occurrenceLimit',
            'aggregateLimit',
            'premium',
          ];
      }
    },
    badgeCount() {
      let cnt = 0;
      for (const key in this.extraFilters) {
        if ((this.extraFilters as Record<string, boolean>)[key]) {
          cnt++;
        }
      }

      if (this.showExpired) {
        cnt++;
      }

      if ((this.daysUntilExpiration || 0) > 0) {
        cnt++;
      }

      return cnt;
    },
    retentions() {
      return PoliciesAsHierarchy(this.allPolicies)
        .filter((p) => p.retention)
        .map((p) => p.retention) as number[];
    },
    searchParams() {
      const sort = Array.isArray(this.sorts) && this.sorts.length === 1 ? this.sorts[0] : undefined;
      const params = {
        startDate: this.startDate,
        endDate: this.endDate,
        limit: this.pageSize + 1,
        offset: (this.currentPage - 1) * this.pageSize,
        parentOnly: true,
        sortField: sort?.key,
        sortDirection: sort?.direction,
        showExternal: this.showExternal,
        activeOnly: this.showCurrentOnly,
        coverageType: this.filteredCoverageTypes,
        topLevelIndustry: this.filteredSicIndustries,
        industry: this.filteredIndustries,
        includeIndustryChildren: this.includeIndustryChildren,
        wholesalerGroup: this.filteredWholesalerGroups,
        carrier: this.filteredCarriers,
        attachmentPointValueMin: this.attachmentRange?.min,
        attachmentPointValueMax: this.attachmentRange?.max,
        retentionMin: this.retentionRange?.min,
        retentionMax: this.retentionRange?.max,
        accountAttributes: this.filteredAccountAttributes,
        agency: this.filteredAgencies,
        ownershipType: this.ownershipType,
        runoffFlag: this.runoffFlag,
        peFlag: this.peFlag,
        daysUntilExpiration: this.daysUntilExpirationValue,
        searchQuery: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchQuery,
        searchField: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchField,
        servicingRolesSearch: this.$props.searchField === 'servicingRoles' ? this.$props.searchQuery : '',
        accountStates: this.filteredAccountStates,
        departments: this.filteredDepartments,
      } as PolicyAllParams;

      return params;
    },
    wholesalerParams() {
      const sort = Array.isArray(this.sorts) ? this.sorts[0] : undefined;
      return {
        includeExpired: !this.showCurrentOnly,
        showExternal: this.showExternal,
        limit: this.pageSize + 1,
        offset: (this.currentPage - 1) * this.pageSize,
        sortField: sort?.key,
        sortDirection: sort?.direction,
        searchQuery: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchQuery,
        searchField: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchField,
        servicingRolesSearch: this.$props.searchField === 'servicingRoles' ? this.$props.searchQuery : '',
        industries: this.filteredIndustries,
        includeIndustryChildren: this.includeIndustryChildren,
        hideUnpublishedRoles: IsUserAClient(store.state.user),
      } as WholesalerGroupPoliciesByIdParams;
    },
    carrierParams() {
      const sort = Array.isArray(this.sorts) ? this.sorts[0] : undefined;
      return {
        includeExpired: !this.showCurrentOnly,
        showExternal: this.showExternal,
        limit: this.pageSize + 1,
        offset: (this.currentPage - 1) * this.pageSize,
        sortField: sort?.key,
        sortDirection: sort?.direction,
        searchQuery: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchQuery,
        searchField: this.$props.searchField === 'servicingRoles' ? '' : this.$props.searchField,
        servicingRolesSearch: this.$props.searchField === 'servicingRoles' ? this.$props.searchQuery : '',
        industries: this.filteredIndustries,
        includeIndustryChildren: this.includeIndustryChildren,
        hideUnpublishedRoles: IsUserAClient(store.state.user),
      } as CarrierPoliciesByIdParams;
    },
    isParent() {
      return this.scenario === policyDetailsType.ParentAccount;
    },
    staticMaxRetention() {
      if (!this.policyStats) return undefined;
      return this.policyStats.maxRetention;
    },
    staticMaxAttachmentPoint() {
      if (!this.policyStats) return undefined;
      return this.policyStats.maxAttachmentPoint;
    },
    routeId() {
      return this.$route.params.id as string;
    },
    downloadMenuOptions() {
      return [
        {
          label: 'Download All Columns',
          handler: () => {
            this.downloadPolicies(false);
          },
        },
        {
          label: 'Download Selected Columns',
          handler: () => {
            this.downloadPolicies(true);
          },
        },
      ];
    },
    nonChildPolicies() {
      return this.allPolicies.filter((p) => !p.isChildPolicy);
    },
  },
  methods: {
    tenantName,
    search(searchText: string) {
      if (!searchText) {
        this.policies = this.allPolicies;
      }

      const searchableFields = [
        'coverageType',
        'description',
        'carrierGroup',
        'policyID',
        'effectiveDate',
        'expirationDate',
      ];

      if (this.searchTimeoutId) {
        clearTimeout(this.searchTimeoutId);
      }

      this.searchTimeoutId = window.setTimeout(() => {
        this.policies = this.allPolicies.filter((policy) => {
          for (const fieldName of searchableFields) {
            let fieldValue = policy[fieldName];
            if (typeof fieldValue === 'string') {
              if (['effectiveDate', 'expirationDate'].includes(fieldName)) {
                fieldValue = DateTime.fromISO(fieldValue, { zone: 'utc' }).toFormat('L/d/yy');
              }

              if ((fieldValue as string).toLowerCase().trim().includes(searchText.toLowerCase())) {
                return true;
              }
            }
          }

          return false;
        });
      }, 350);
    },
    sortByField(field: string) {
      const sort = this.sorts.find((s) => s.key === field);
      if (sort) {
        // sort field already exists. need to flip the direction
        sort.direction = sort.direction === 'ascending' ? 'descending' : 'ascending';
        this.sorts = [sort];
      } else {
        this.sorts = [{ key: field, direction: 'ascending' }];
      }
      // not sorted by this field already
      this.currentPage = 1;
      this.refreshPolicies();
    },
    filterPolicies() {
      this.loading = true;
      if (this.scenario !== policyDetailsType.All || Array.isArray(this.preloadedPolicies)) {
        this.policies = Array.isArray(this.allPolicies) ? GetPoliciesAsHierarchy(this.allPolicies.slice()) : [];
      }
      this.loading = false;
    },
    previousPage() {
      this.currentPage--;
      window.scrollTo(0, 0);
      if (this.isPaged) {
        this.refreshPolicies();
      } else {
        this.filterPolicies();
      }
    },
    nextPage() {
      this.currentPage++;
      window.scrollTo(0, 0);
      if (this.isPaged) {
        this.refreshPolicies();
      } else {
        this.filterPolicies();
      }
    },
    clearFilter() {
      this.moreFilters = {
        showExternal: false,
        showExpired: false,
        daysUntilExpiration: null as number | null,
        showPrimary: false,
        showExcess: false,
        showRunoff: false,
        showNoRunoff: false,
        showFavorites: false,
      };
    },
    resetFilter(category: string, filter: FilterField<FilterValueType> | undefined) {
      switch (category) {
        case FilterCoverageType:
          this.selectedCoverageTypes = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedCoverageTypes = this.selectedCoverageTypes;
          break;

        case FilterSICIndustry:
          this.selectedSicIndustries = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedSicIndustries = this.selectedSicIndustries;
          break;

        case FilterIndustry:
          this.selectedIndustries = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedIndustries = this.selectedIndustries;
          this.includeIndustryChildren = filter ? !!filter.options?.find((o) => o.label === 'Include Children') : false;
          break;

        case FilterWholesalerGroup:
          this.selectedWholesalerGroups = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedWholesalerGroups = this.selectedWholesalerGroups;
          break;

        case FilterCarrierGroup:
          this.selectedCarriers = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedCarriers = this.selectedCarriers;
          break;

        case FilterClientAttributes:
          this.selectedAccountAttributes = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedAccountAttributes = this.selectedAccountAttributes;
          break;

        case FilterRetention: {
          const range = filter?.range;
          this.selectedRetentionRange = range || [];
          this.appliedRetentionRange = this.selectedRetentionRange;
          break;
        }

        case FilterAttachment: {
          const range = filter?.range;
          this.selectedAttachmentRange = range || [];
          this.appliedAttachmentRange = this.selectedAttachmentRange;
          break;
        }

        case FilterCACEntity:
          this.selectedAgencies = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedAgencies = this.selectedAgencies;
          break;

        case FilterAccountStates: {
          this.selectedAccountStates = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedAccountStates = this.selectedAccountStates;
          break;
        }

        case FilterDepartments: {
          this.selectedDepartments = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedDepartments = this.selectedDepartments;
          break;
        }

        case FilterOwnershipType: {
          this.selectedOwnershipTypes = filter ? filter.values.map((f) => f.value as string) : [];
          this.appliedOwnershipTypes = this.selectedOwnershipTypes;
          break;
        }
      }
    },
    resetMoreOptions(moreOptions: FilterMoreOptions | undefined) {
      this.appliedFilterBadges = [];

      if (!moreOptions) {
        this.daysUntilExpiration = null;
        this.extraFilters.showRunoff = false;
        this.extraFilters.showNoRunoff = false;
        this.extraFilters.showExcess = false;
        this.extraFilters.showPrimary = false;

        return;
      }

      let appliedCount = 0;

      if (moreOptions.daysUntilExpiration) {
        this.daysUntilExpiration = moreOptions.daysUntilExpiration;
        appliedCount++;
      }
      if (moreOptions.runoff) {
        this.extraFilters.showRunoff = moreOptions.runoff;
        appliedCount++;
      }
      if (moreOptions.noRunoff) {
        this.extraFilters.showNoRunoff = moreOptions.noRunoff;
        appliedCount++;
      }
      if (moreOptions.excess) {
        this.extraFilters.showExcess = moreOptions.excess;
        appliedCount++;
      }
      if (moreOptions.primary) {
        this.extraFilters.showPrimary = moreOptions.primary;
        appliedCount++;
      }

      this.appliedFilterBadges.push({
        category: 'More Options',
        count: appliedCount,
      });
    },
    async removeFilter(category: string) {
      // More Options filter removal

      if (category === FilterMoreOptions) {
        this.appliedOptions = undefined;
        this.resetMoreOptions(this.appliedOptions);
        this.clearFilter();

        const badgeIndex = this.appliedFilterBadges.findIndex((b) => b.category === category);
        if (badgeIndex > -1) {
          this.appliedFilterBadges.splice(badgeIndex, 1);
          this.appliedFilterBadges = [...this.appliedFilterBadges];
        }

        await this.refreshPolicies();
        return;
      }

      // everything else

      const index = this.appliedFilters.findIndex((f) => f.category === category);

      if (index > -1) {
        this.appliedFilters.splice(index, 1);
        this.appliedFilters = [...this.appliedFilters];

        this.resetFilter(category, undefined);

        const badgeIndex = this.appliedFilterBadges.findIndex((b) => b.category === category);
        if (badgeIndex > -1) {
          this.appliedFilterBadges.splice(badgeIndex, 1);
          this.appliedFilterBadges = [...this.appliedFilterBadges];
        }
      }

      await this.setQueryParamsFromFilters();
      this.currentPage = 1;
      this.refreshPolicies();
    },
    async activateFilter(category: string) {
      this.filterOpen = true;
      this.activeFilterCategory = category;
    },
    async applyFilters(filters: FilterField<FilterValueType>[], moreOptions?: FilterMoreOptions) {
      const filterCategories = [
        FilterCoverageType,
        FilterSICIndustry,
        FilterIndustry,
        FilterWholesalerGroup,
        FilterCarrierGroup,
        FilterAttachment,
        FilterRetention,
        FilterClientAttributes,
        FilterCACEntity,
        FilterAccountStates,
        FilterDepartments,
        FilterOwnershipType,
      ];

      // reassign values to legacy filter refs

      filterCategories.forEach((category) => {
        const filter = filters.find((f) => f.category === category);
        this.resetFilter(category, filter);
      });

      // handle more options (legacy "More Filters" filter)

      if (
        // this if/else was ported from old "More Filters" -> Apply; not totally certain how this is used
        this.showExpiredApplied !== this.moreFilters.showExpired ||
        this.showExternalApplied !== this.moreFilters.showExternal
      ) {
        if (
          this.showExternalApplied !== this.moreFilters.showExternal &&
          this.scenario === policyDetailsType.ParentAccount
        ) {
          this.$emit('change-external', this.moreFilters.showExternal);
        }

        this.showExpiredApplied = this.moreFilters.showExpired;
        this.showExternalApplied = this.moreFilters.showExternal;

        this.showExpired = this.moreFilters.showExpired;
        this.showExternal = this.moreFilters.showExternal;
      }

      this.daysUntilExpiration = moreOptions?.daysUntilExpiration ?? null;
      this.extraFilters.showRunoff = moreOptions?.runoff ?? false;
      this.extraFilters.showNoRunoff = moreOptions?.noRunoff ?? false;
      this.extraFilters.showExcess = moreOptions?.excess ?? false;
      this.extraFilters.showPrimary = moreOptions?.primary ?? false;

      this.appliedOptions = moreOptions;
      this.appliedFilters = filters;

      this.currentPage = 1;
      await this.setQueryParamsFromFilters();
      await this.refreshPolicies();
    },
    async applyFields(newFields: string[]) {
      this.appliedFields = newFields;
      await this.setQueryParamsFromFilters();
    },
    async expand(open: boolean, policy: PolicyWithChildren) {
      policy.expanded = !policy.expanded;
      if (!open || !policy.uniqPolicy) return;

      if (policy.hasChildren && !policy.children) {
        const children = await policyService.children(policy.uniqPolicy);

        this.policies.forEach((p) => {
          if (p.uniqPolicy === policy.uniqPolicy) {
            p.children = children;
          }
        });
      }
    },
    applyPolicyScopeFilter() {
      if (this.preloadedPolicies) {
        this.$emit('policy-details-refresh', {
          showExternal: this.showExternal,
          showCurrentOnly: this.showCurrentOnly,
          showWithoutProgramOnly: this.showWithoutProgramOnly,
        } as PolicyDetailsFilter);
      }
      this.refreshPolicies();
    },
    async externalPolicyUploaded() {
      this.showExternal = true;
      await this.refreshPolicies();
    },
    async refreshPolicies(): Promise<void> {
      this.loading = true;
      await this.setQueryParamsFromFilters();

      if (this.preloadedPolicies) {
        this.allPolicies = this.preloadedPolicies;
      } else {
        switch (this.scenario) {
          case policyDetailsType.All: {
            const policiesWithStats = await policyService.all(this.searchParams);
            this.policyStats = policiesWithStats.stats;
            this.allPolicies = (policiesWithStats.policies || []).map(formatAddress);

            this.setPageFromPolicies();
            this.policies = this.allPolicies;

            break;
          }
          case policyDetailsType.Account: {
            this.allPolicies = (
              await accountService.policiesById(
                this.routeId,
                !this.showCurrentOnly,
                this.showWithoutProgramOnly,
                this.showExternal,
                this.hideUnpublishedRoles,
                this.filteredCoverageTypes,
              )
            ).map(formatAddress);

            break;
          }
          case policyDetailsType.ParentAccount: {
            this.allPolicies = (
              await parentAccountService.policiesById(
                this.routeId,
                !this.showCurrentOnly,
                !this.showExternal,
                this.hideUnpublishedRoles,
                this.filteredCoverageTypes ?? [],
              )
            ).map(formatAddress);

            break;
          }
          case policyDetailsType.Carrier: {
            this.allPolicies = (await carrierService.policiesById(this.routeId, this.carrierParams)).map(formatAddress);

            this.setPageFromPolicies();
            this.policies = this.allPolicies;

            break;
          }
          case policyDetailsType.CustomWholesaler: {
            this.allPolicies = (
              await wholesalerGroupService.policiesByCustomGroup(this.routeId, this.wholesalerParams)
            ).map(formatAddress);

            this.setPageFromPolicies();
            this.policies = this.allPolicies;

            break;
          }
          case policyDetailsType.Program: {
            break;
          }
          default: {
            // wholesaler
            this.allPolicies = (await wholesalerGroupService.policiesById(this.routeId, this.wholesalerParams)).map(
              formatAddress,
            );

            this.setPageFromPolicies();
            this.policies = this.allPolicies;

            break;
          }
        }
      }
      this.filterPolicies();

      this.loading = false;
      await this.$nextTick();
      dispatchEvent(new Event('resize'));
    },
    setPageFromPolicies() {
      this.hasPrevPage = !!(this.currentPage - 1);
      this.hasNextPage = this.nonChildPolicies.length > this.pageSize;
      if (this.nonChildPolicies.length > this.pageSize) {
        //eslint-disable-next-line
        //@ts-ignore-next-line
        const lastNonChildPolicyIndex = (this.allPolicies as Array<EpicPolicy>).findLastIndex(
          (policy) => !policy.isChildPolicy,
        );
        if (lastNonChildPolicyIndex !== -1) {
          this.allPolicies.splice(lastNonChildPolicyIndex, 1);
        }
      }
    },
    buildDlpAuditConfig(metadata: Record<string, unknown>) {
      return {
        headers: {
          'X-DLP-Audit-Category': 'POLICY_EXPLORER_DOWNLOAD',
          'X-DLP-Audit-Metadata': JSON.stringify(metadata),
        },
      };
    },
    async downloadPolicies(displayedColumns: boolean) {
      let flattenedPolicies: EpicPolicy[];
      let parentView: boolean;
      let downloadName: string;

      const exportColumns = displayedColumns ? this.appliedFields : undefined;

      // Determine base arguments based on the scenario

      if (this.scenario === policyDetailsType.Carrier) {
        const { name } = await carrierService.findById(this.routeId);
        // Fetch all policies for download, not just the visible ones
        const allCarrierPolicies = await carrierService.policiesById(
          this.routeId, {
            ...this.carrierParams,
            limit: 999_999_999,
            offset: 0,
          });
        flattenedPolicies = FlattenPolicies(allCarrierPolicies.map(formatAddress)).map((p) => ({
          ...p,
          carrierGroup: name,
        }));
        parentView = false;
        downloadName = name;
      } else if (this.scenario === policyDetailsType.Wholesaler) {
        const wholesalerGroup = await wholesalerGroupService.findById(this.routeId);
        // Fetch all policies for download, not just the visible ones
        const allWholesalerPolicies = await wholesalerGroupService.policiesById(
          this.routeId, {
            ...this.wholesalerParams,
            limit: 999_999_999,
            offset: 0,
          });
        flattenedPolicies = FlattenPolicies(allWholesalerPolicies.map(formatAddress));
        parentView = false;
        downloadName = wholesalerGroup.name;
      } else if (this.scenario === policyDetailsType.CustomWholesaler) {
        const customWholesalerGroup = await wholesalerGroupService.customGroupById(this.routeId);
        // Fetch all policies for download, not just the visible ones
        const allCustomWholesalerPolicies = await wholesalerGroupService.policiesByCustomGroup(
          this.routeId, {
            ...this.wholesalerParams,
            limit: 999_999_999,
            offset: 0,
          });
        flattenedPolicies = FlattenPolicies(allCustomWholesalerPolicies.map(formatAddress));
        parentView = false;
        downloadName = customWholesalerGroup.name;
      } else if (
        [policyDetailsType.Account, policyDetailsType.ParentAccount].includes(this.scenario as policyDetailsType)
      ) {
        const account = await accountService.findById(this.routeId);
        flattenedPolicies = FlattenPolicies(this.policies);
        parentView = this.scenario === policyDetailsType.ParentAccount;
        downloadName = account.accountName;
      } else if (this.scenario === policyDetailsType.Program) {
        flattenedPolicies = this.preloadedPolicies ?? [];
        parentView = this.scenario === policyDetailsType.Program;
        downloadName = 'Policies';
      } else {
        // all (Policy Explorer)
        const dlpConfig = this.buildDlpAuditConfig({
          exportFormat: 'csv',
          columns: exportColumns,
          filterCriteria: {
            dateRange: `${this.searchParams.startDate} to ${this.searchParams.endDate}`,
            coverageTypes: this.filteredCoverageTypes,
            industries: this.filteredIndustries,
            wholesalerGroups: this.filteredWholesalerGroups,
            carriers: this.filteredCarriers,
            accountStates: this.filteredAccountStates,
            departments: this.filteredDepartments,
          },
        });
        const policiesWithStats = await policyService.all(
          {
            ...this.searchParams,
            parentOnly: !this.includeCoverageLines,
            limit: 999_999_999,
          },
          dlpConfig,
        );
        flattenedPolicies = policiesWithStats.policies.map(formatAddress);
        parentView = this.scenario === policyDetailsType.ParentAccount;
        downloadName = 'Policies';
      }

      // filter out child policies if toggle is off (for 'all' scenario, these are already filtered out)
      if (this.scenario !== policyDetailsType.All && !this.includeCoverageLines) {
        flattenedPolicies = flattenedPolicies.filter((p) => !p.isChildPolicy);
      }

      if (displayedColumns) {
        DownloadCSV(TransformPoliciesToCSV(flattenedPolicies, parentView, this.appliedFields), downloadName);
      } else {
        DownloadCSV(TransformPoliciesToCSV(flattenedPolicies, parentView), downloadName);
      }
    },
    setFiltersFromQueryParams(): void {
      const {
        col,
        cov,
        sic,
        ind,
        who,
        cg,
        at,
        r,
        ca,
        ext,
        exp,
        due,
        own,
        run_y,
        run_n,
        pri,
        exs,
        cur,
        ag,
        dept,
      } = this.$route.query;

      // columns filter
      if (col && typeof col === 'string') {
        this.appliedFields = col.split(',').map((i) => this.orderedColumnValues[parseInt(i)]);
      } else {
        this.appliedFields = this.defaultFields;
      }

      this.appliedFilters = [];

      // coverage types filter
      if (cov && typeof cov === 'string') {
        this.appliedCoverageTypes = cov.split(',').map((i) => this.coverageTypeValues[parseInt(i)]);
        this.selectedCoverageTypes = this.appliedCoverageTypes;
        this.appliedFilters.push({
          category: FilterCoverageType,
          values: this.appliedCoverageTypes.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedCoverageTypes = [];
      }

      // SIC industries filter
      if (sic && typeof sic === 'string') {
        this.appliedSicIndustries = sic.split(',').map((i) => this.sicIndustriesValues[parseInt(i)]);
        this.selectedSicIndustries = this.appliedSicIndustries;
        this.appliedFilters.push({
          category: FilterSICIndustry,
          values: this.appliedSicIndustries.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedSicIndustries = [];
      }

      // industries filter
      if (ind && typeof ind === 'string') {
        this.appliedIndustries = ind.split(',').map((i) => this.industriesValues[parseInt(i)]);
        this.selectedIndustries = this.appliedIndustries;
        this.appliedFilters.push({
          category: FilterIndustry,
          values: this.appliedIndustries.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedIndustries = [];
      }

      // wholesaler group filter
      if (who && typeof who === 'string') {
        this.appliedWholesalerGroups = who.split(',').map((i) => this.wholesalerGroupsValues[parseInt(i)]);
        this.selectedWholesalerGroups = this.appliedWholesalerGroups;
        this.appliedFilters.push({
          category: FilterWholesalerGroup,
          values: this.appliedWholesalerGroups.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedWholesalerGroups = [];
      }

      // carrier groups filter
      if (cg && typeof cg === 'string') {
        this.appliedCarriers = cg.split(',').map((i) => this.carrierGroupValues[parseInt(i)]);
        this.selectedCarriers = this.appliedCarriers;
        this.appliedFilters.push({
          category: FilterCarrierGroup,
          values: this.appliedCarriers.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedCarriers = [];
      }

      // attachment point filter
      if (at && typeof at === 'string') {
        this.appliedAttachmentRange = at.split(',').map((i) => parseInt(i));
        this.selectedAttachmentRange = this.appliedAttachmentRange;
        this.appliedFilters.push({
          category: FilterAttachment,
          values: [],
          range: [this.appliedAttachmentRange[0], this.appliedAttachmentRange[1]],
        });
      } else {
        this.appliedAttachmentRange = [];
      }

      // retention filter
      if (r && typeof r === 'string') {
        this.appliedRetentionRange = r.split(',').map((i) => parseInt(i));
        this.selectedRetentionRange = this.appliedRetentionRange;
        this.appliedFilters.push({
          category: FilterAttachment,
          values: [],
          range: [this.appliedRetentionRange[0], this.appliedRetentionRange[1]],
        });
      } else {
        this.appliedRetentionRange = [];
      }

      // client attributes filter
      if (ca && typeof ca === 'string') {
        this.appliedAccountAttributes = ca.split(',').map((i) => this.clientAttributesValues[parseInt(i)]);
        this.selectedAccountAttributes = this.appliedAccountAttributes;
        this.appliedFilters.push({
          category: FilterClientAttributes,
          values: this.appliedAccountAttributes.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedAccountAttributes = [];
      }

      // agency filter
      if (ag && typeof ag === 'string') {
        this.appliedAgencies = ag.split(',').map((i) => this.agenciesValues[parseInt(i)]);
        this.selectedAgencies = this.appliedAgencies;
        this.appliedFilters.push({
          category: FilterCACEntity,
          values: this.agenciesValues.map((c) => ({ value: c, label: c })),
        });
      } else {
        this.appliedAgencies = [];
      }

      // departments filter
      if (dept && typeof dept === 'string') {
        this.appliedDepartments = dept.split(',').map((i) => this.departmentsValues[parseInt(i)]);
        this.selectedDepartments = this.appliedDepartments;
        this.appliedFilters.push({
          category: FilterDepartments,
          values: this.appliedDepartments.map((d) => ({ value: d, label: d })),
        });
      } else {
        this.appliedDepartments = [];
      }

      // ownership type filter
      if (own && typeof own === 'string') {
        this.appliedOwnershipTypes = own.split(',');
        this.selectedOwnershipTypes = this.appliedOwnershipTypes;
        this.appliedFilters.push({
          category: FilterOwnershipType,
          values: this.appliedOwnershipTypes.map((ot) => ({ value: ot, label: ot })),
        });
      } else {
        this.appliedOwnershipTypes = [];
      }

      // show external policies
      if (ext) {
        this.showExternal = true;
        this.showExternalApplied = true;
      }

      // show expired policies
      if (exp) {
        this.showExpired = true;
        this.showExpiredApplied = true;
      }

      // show polcies with specified days until expiration
      if (due && typeof due === 'string') {
        const daysUntilExpiration = parseInt(due);

        if (isNaN(daysUntilExpiration)) {
          this.daysUntilExpiration = null;
        } else {
          this.daysUntilExpiration = daysUntilExpiration;

          if (!this.appliedOptions) this.appliedOptions = {};
          this.appliedOptions.daysUntilExpiration = daysUntilExpiration;
        }
      }

      // show policies WITH runoff
      if (run_y) {
        this.extraFilters.showRunoff = true;
        this.moreFilters.showRunoff = true;

        if (!this.appliedOptions) this.appliedOptions = {};
        this.appliedOptions.runoff = true;
      }

      // show policies WITHOUT runoff
      if (run_n) {
        this.extraFilters.showNoRunoff = true;
        this.moreFilters.showNoRunoff = true;

        if (!this.appliedOptions) this.appliedOptions = {};
        this.appliedOptions.noRunoff = true;
      }

      // show primary policies
      if (pri) {
        this.extraFilters.showPrimary = true;
        this.moreFilters.showPrimary = true;

        if (!this.appliedOptions) this.appliedOptions = {};
        this.appliedOptions.primary = true;
      }

      // show excess policies
      if (exs) {
        this.extraFilters.showExcess = true;
        this.moreFilters.showExcess = true;

        if (!this.appliedOptions) this.appliedOptions = {};
        this.appliedOptions.excess = true;
      }

      // show current policies only
      if (cur) {
        this.showCurrentOnly = cur === 'true';
      } else {
        this.showCurrentOnly = this.showExpiredAtStart ? false : true;
      }

      this.applyPolicyScopeFilter();
    },
    async setQueryParamsFromFilters(): Promise<void> {
      const query: QueryParams = {};

      if (this.startDate) {
        query.start = this.startDate;
      }

      if (this.endDate) {
        query.end = this.endDate;
      }

      if (this.appliedFields) {
        query.col = this.appliedFields.map((f) => this.orderedColumnValues.indexOf(f)).join(',');
      }

      if (this.appliedCoverageTypes && this.appliedCoverageTypes.length > 0) {
        query.cov = this.appliedCoverageTypes.map((ct) => ct && this.coverageTypeValues.indexOf(ct)).join(',');
      }

      if (this.appliedSicIndustries && this.appliedSicIndustries.length > 0) {
        query.sic = this.appliedSicIndustries.map((ct) => ct && this.sicIndustriesValues.indexOf(ct)).join(',');
      }
      if (this.appliedIndustries && this.appliedIndustries.length > 0) {
        query.ind = this.appliedIndustries.map((ct) => ct && this.industriesValues.indexOf(ct)).join(',');
      }

      if (this.appliedWholesalerGroups && this.appliedWholesalerGroups.length > 0) {
        query.wg = this.appliedWholesalerGroups.map((wg) => wg && this.wholesalerGroupsValues.indexOf(wg)).join(',');
      }

      if (this.appliedCarriers && this.appliedCarriers.length > 0) {
        query.cg = this.appliedCarriers.map((ct) => ct && this.carrierGroupValues.indexOf(ct)).join(',');
      }

      if (this.appliedAttachmentRange && this.appliedAttachmentRange.length > 0) {
        query.at = this.appliedAttachmentRange.join(',');
      }

      if (this.appliedRetentionRange && this.appliedRetentionRange.length > 0) {
        query.r = this.appliedRetentionRange.join(',');
      }

      if (this.appliedAccountAttributes && this.appliedAccountAttributes.length > 0) {
        query.ca = this.appliedAccountAttributes.map((a) => a && this.clientAttributesValues.indexOf(a)).join(',');
      }

      if (this.appliedAgencies && this.appliedAgencies.length > 0) {
        query.ag = this.appliedAgencies.map((a) => a && this.agenciesValues.indexOf(a)).join(',');
      }

      if (this.appliedDepartments && this.appliedDepartments.length > 0) {
        query.dept = this.appliedDepartments.map((d) => d && this.departmentsValues.indexOf(d)).join(',');
      }

      if (this.appliedOwnershipTypes && this.appliedOwnershipTypes.length > 0) {
        query.own = this.appliedOwnershipTypes.join(',');
      }

      if (this.showExternal) {
        query.ext = 'true';
      }

      if (this.showExpired) {
        query.exp = 'true';
      }

      if (this.daysUntilExpiration && this.daysUntilExpiration > 0) {
        query.due = this.daysUntilExpiration.toString();
      }

      if (this.extraFilters.showRunoff) {
        query.run_y = 'true';
      }

      if (this.extraFilters.showNoRunoff) {
        query.run_n = 'true';
      }

      if (this.extraFilters.showPrimary) {
        query.pri = 'true';
      }

      if (this.extraFilters.showExcess) {
        query.exs = 'true';
      }

      query.cur = this.showCurrentOnly ? 'true' : 'false';

      if (Object.keys(query).length) {
        if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
          await this.$router.replace({ query } as RouteLocationRaw).catch(() => {
            return '';
          });
        }
      }
    },
    recomputeFilterBadges() {
      this.appliedFilterBadges = this.appliedFilters.map((f) => {
        let count = 0;

        switch (f.category) {
          case FilterRetention:
          case FilterAttachment:
            if (f.range) count = 1;
            break;
          default:
            count = f.values.length;
        }

        return { category: f.category, count };
      });

      if (this.appliedOptions) {
        let appliedCount = 0;

        if (this.appliedOptions.daysUntilExpiration) appliedCount++;
        if (this.appliedOptions.runoff) appliedCount++;
        if (this.appliedOptions.noRunoff) appliedCount++;
        if (this.appliedOptions.excess) appliedCount++;
        if (this.appliedOptions.primary) appliedCount++;

        if (appliedCount > 0) {
          this.appliedFilterBadges.push({ category: FilterMoreOptions, count: appliedCount });
        }
      }
    },
  },
  watch: {
    preloadedPolicies() {
      this.allPolicies = this.preloadedPolicies || [];
      this.filterPolicies();
    },
    startDate() {
      this.currentPage = 1;
      this.refreshPolicies();
    },
    endDate() {
      this.currentPage = 1;
      this.refreshPolicies();
    },
    showExpired(show: boolean) {
      this.currentPage = 1;
      this.showCurrentOnly = !show;
    },
    showCurrentOnly() {
      this.currentPage = 1;
      this.applyPolicyScopeFilter();
    },
    showExternal() {
      this.currentPage = 1;
      this.applyPolicyScopeFilter();
    },
    showWithoutProgramOnly() {
      this.currentPage = 1;
      this.applyPolicyScopeFilter();
    },
    clientViewEnabled() {
      this.appliedFields = this.defaultFields;
    },
    searchQuery() {
      this.currentPage = 1;
      this.refreshPolicies();
    },
    searchField() {
      if (this.searchQuery?.length > 0) {
        this.currentPage = 1;
        this.refreshPolicies();
      }
    },
    appliedFilters() {
      this.recomputeFilterBadges();
    },
    appliedOptions() {
      this.recomputeFilterBadges();
    },
  },
  mounted() {
    registerResizeItem({ name: 'policy-details-subheader', selector: 'policy-details-subheader' });
    registerResizeItem({ name: 'policy-pagination-buttons', selector: 'policy-pagination-buttons' });

    store.subscribe((mutation: MutationPayload) => {
      if (
        mutation.type === StoreMutationType.ENABLE_CLIENT_VIEW_MODE ||
        mutation.type === StoreMutationType.DISABLE_CLIENT_VIEW_MODE
      ) {
        this.appliedFields = this.defaultFields;
      }
    });
  },
  async beforeMount() {
    if (this.scenario === policyDetailsType.Account || this.scenario === policyDetailsType.ParentAccount) {
      const [accountInfo, coverageTypes] = await Promise.all([
        accountService.findById(this.routeId),
        accountServiceV2.getCoverageTypes(Number(this.routeId)),
      ]);

      this.coverageTypes = coverageTypes
        ?.map((ct) => ({ value: ct, label: ct }))
        .sort((a, b) => a.label.localeCompare(b.label));

      if (accountInfo.accountStatus === 'P') {
        this.showCurrentOnly = false;
        this.showExternal = true;
      }
    }

    if (this.scenario === policyDetailsType.All && !this.preloadedPolicies) {
      const [
        coverageTypes,
        topLevelIndustries,
        wholesalerGroups,
        carriers,
        accountAttributes,
        policyDocumentTypes,
        agencyTypes,
        departments,
      ] = await Promise.all([
        referenceService.coverageTypes(),
        referenceService.topLevelIndustries(),
        wholesalerGroupService.list(),
        referenceService.carrierGroups(),
        referenceService.accountAttributeTypes(),
        documentService.getDocumentTypes(),
        referenceService.agencyTypes(),
        referenceService.departments(),
      ]);

      this.allCoverageTypes = coverageTypes
        .map((c) => c.label)
        .map((c) => ({ text: c, value: c }))
        .sort((a, b) => a.text.localeCompare(b.text));

      this.coverageTypes = this.allCoverageTypes.map((ct) => ({ value: ct.value, label: ct.text }));

      this.allSicIndustries = topLevelIndustries
        .map((i) => i.Name)
        .map((i) => ({ text: i, value: i }))
        .sort((a, b) => a.text.localeCompare(b.text));

      this.allWholesalerGroups = wholesalerGroups
        .map((w) => ({ text: w.name.trim(), value: w.name.trim() }))
        .sort((a, b) => a.text.localeCompare(b.text));

      this.allCarriers = carriers
        .map((c) => c.label)
        .map((c) => ({ text: c, value: c }))
        .sort((a, b) => a.text.localeCompare(b.text));

      this.accountAttributeTypes = accountAttributes.map((a) => a.name).map((a) => ({ label: a, value: a }));
      this.policyDocumentTypes = policyDocumentTypes.filter((t) => t.domainName === 'Policy');
      this.allAgencies = agencyTypes.map((a) => ({ label: a.agencyName, value: a.agencyName }));
      this.allDepartments = departments
        .map((d) => ({ value: d.label, label: d.label }))
        .sort((a, b) => a.label.localeCompare(b.label));
      this.listOfUSStates = USStates;
    } else {
      this.policyDocumentTypes = (await documentService.getDocumentTypes()).filter((t) => t.domainName === 'Policy');
    }

    if (this.allowIndustryFilter) {
      const industries = await referenceService.industries();
      this.allIndustries = industries
        .map((i) => i.name)
        .map((i) => ({ text: i, value: i }))
        .sort((a, b) => a.text.localeCompare(b.text));
    }

    this.policySources = await policySourceService.list();

    this.setFiltersFromQueryParams();

    return;
  },
};
</script>

<style scoped>
.wrapper {
  border-bottom: 1px solid #cccccc;
  margin: 0 1em;
}

.wrapper:last-child {
  border-bottom: none;
}
</style>
